import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Progress,
  Result,
  Row,
  Select,
  Skeleton,
  Space,
  Steps,
  Table,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SwapOutlined,
  TeamOutlined,
  TrophyOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { getCall } from "../../constants/apicalls.constant";
import { useCorral } from "./hooks/useCorral";
import {
  CorralAssignment,
  CorralBreakdownItem,
  CorralDefinition,
  CorralStrategy,
  EditableCorral,
} from "./types/corral.types";
import { computeCorralGroups } from "./utils/corral.utils";
import CorralEstimation from "./components/CorralEstimation";
import CorralAssignmentStep from "./components/CorralAssignmentStep";
import CorralPreview from "./components/CorralPreview";
import CreateCorralModal from "./components/CreateCorralModal";
import DistanceFilterCards from "../bib/components/DistanceFilterCards";
import { isEventPastOrArchived } from "../../utils/eventDisplay";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

const { Text, Title } = Typography;

type WizardStep = 0 | 1 | 2;

const CORRAL_PALETTE = [
  { bg: "#f0fdf4", border: "#86efac", accent: "#16a34a", text: "#166534" },
  { bg: "#eff6ff", border: "#93c5fd", accent: "#2563eb", text: "#1d4ed8" },
  { bg: "#fff7ed", border: "#fdba74", accent: "#ea580c", text: "#9a3412" },
  { bg: "#faf5ff", border: "#d8b4fe", accent: "#7c3aed", text: "#581c87" },
  { bg: "#fff1f2", border: "#fda4af", accent: "#e11d48", text: "#9f1239" },
  { bg: "#f0fdfa", border: "#5eead4", accent: "#0d9488", text: "#115e59" },
];

const CorralPage: React.FC = () => {
  const { id: eventId } = useParams();

  const [viewMode, setViewMode] = useState<"loading" | "manage" | "wizard">(
    "loading",
  );

  // Wizard state
  const [wizardStep, setWizardStep] = useState<WizardStep>(0);
  const [confirmed, setConfirmed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [selectedDistance, setSelectedDistance] = useState<
    string | undefined
  >();
  const [strategy, setStrategy] = useState<CorralStrategy>("fixed_size");
  const [fixedSize, setFixedSize] = useState(100);
  const [editableCorrals, setEditableCorrals] = useState<EditableCorral[]>([]);

  // Manage mode state
  const [selectedCorralId, setSelectedCorralId] = useState<
    string | undefined
  >();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [layoutModalOpen, setLayoutModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<string | null>(
    null,
  );
  const [selectedNewCorralId, setSelectedNewCorralId] = useState<string | null>(
    null,
  );

  // Ref for auto-scrolling to assignments table
  const assignmentsRef = useRef<HTMLDivElement>(null);

  // Edit corral modal state
  const [editCorralModal, setEditCorralModal] =
    useState<CorralDefinition | null>(null);
  const [editCorralForm] = Form.useForm();
  const [editCorralSaving, setEditCorralSaving] = useState(false);

  const {
    corrals,
    participants,
    assignments,
    summary,
    bibRanges,
    loadingCorrals,
    loadingParticipants,
    loadingSummary,
    loadingAssignments,
    assigning,
    total,
    assignmentsTotal,
    assignmentsPage,
    assignmentsPageSize,
    fetchCorrals,
    fetchParticipants,
    fetchSummary,
    fetchAssignments,
    fetchBibRanges,
    addCorral,
    editCorral,
    removeCorral,
    runAutoAssign,
    runManualAssign,
    runUnassign,
  } = useCorral();

  const { response: eventResponse, loading: eventLoading } = useAPI<any>(
    ENDPOINTS.event,
    true,
    [eventId],
    getCall({}, eventId),
  );
  const storeEvent = useSelector((state: RootState) => state.event);
  const event = eventResponse?.data;
  const readOnlyAssignments =
    isEventPastOrArchived(storeEvent) || isEventPastOrArchived(event);

  const distances = useMemo<string[]>(() => {
    const tickets: any[] = event?.tickets || [];
    const seen = new Set<string>();
    tickets.forEach((t) => {
      if (t.distance) seen.add(t.distance);
    });
    return Array.from(seen);
  }, [event]);

  // Tickets belonging to the currently selected distance
  const ticketsForSelectedDistance = useMemo(() => {
    const allTickets: any[] = event?.tickets || [];
    return allTickets.filter((t) => t.distance === selectedDistance);
  }, [event, selectedDistance]);

  // Block assignment while any ticket in this distance still has open registration
  const isRegistrationOpen = useMemo(() => {
    return ticketsForSelectedDistance.some((t) => {
      if (!t.registration_end_date) return false;
      const end = dayjs(
        `${t.registration_end_date} ${t.registration_end_time || "23:59:59"}`,
      );
      return end.isValid() && dayjs().isBefore(end);
    });
  }, [ticketsForSelectedDistance]);

  // Latest still-open registration close time, for display
  const latestRegistrationClose = useMemo(() => {
    let latest: Dayjs | null = null;
    ticketsForSelectedDistance.forEach((t) => {
      if (!t.registration_end_date) return;
      const end = dayjs(
        `${t.registration_end_date} ${t.registration_end_time || "23:59:59"}`,
      );
      if (end.isValid() && dayjs().isBefore(end)) {
        if (!latest || end.isAfter(latest)) latest = end;
      }
    });
    return latest ? (latest as Dayjs).format("DD MMM YYYY, hh:mm A") : null;
  }, [ticketsForSelectedDistance]);

  const isFullyAssigned = useMemo(() => {
    return (
      !isRegistrationOpen &&
      (summary?.unassigned ?? 0) === 0 &&
      (summary?.total ?? 0) > 0 &&
      corrals.filter((c) => !selectedDistance || c.distance === selectedDistance).length > 0
    );
  }, [isRegistrationOpen, summary, corrals, selectedDistance]);

  useEffect(() => {
    setSelectedDistance(undefined);
    setViewMode("loading");
    setConfirmed(false);
    setEditableCorrals([]);
    setSelectedCorralId(undefined);
  }, [eventId]);

  // ─── Auto-select first distance when available ────────────────────────────────
  useEffect(() => {
    if (distances.length > 0 && !selectedDistance) {
      setSelectedDistance(distances[0]);
    }
  }, [distances, selectedDistance]);

  // ─── Redirect out of wizard if registration is still open ─────────────────────
  useEffect(() => {
    if (viewMode === "wizard" && isRegistrationOpen) {
      setViewMode("manage");
    }
  }, [viewMode, isRegistrationOpen]);

  useEffect(() => {
    if (!eventId || !selectedDistance) return;

    const loadData = async () => {
      setViewMode("loading");
      const [loadedCorrals] = await Promise.all([
        fetchCorrals(eventId, selectedDistance),
        fetchBibRanges(eventId, selectedDistance),
      ]);
      if (loadedCorrals && loadedCorrals.length > 0) {
        await Promise.all([
          fetchSummary(eventId, selectedDistance),
          fetchAssignments(eventId, selectedDistance),
        ]);
      } else if (!readOnlyAssignments) {
        fetchParticipants(eventId, selectedDistance, 1);
      }
      setViewMode("manage");
    };
    loadData();
  }, [
    eventId,
    selectedDistance,
    readOnlyAssignments,
    fetchCorrals,
    fetchSummary,
    fetchAssignments,
    fetchParticipants,
    fetchBibRanges,
  ]);

  const savedRangesForFilter = useMemo(() => {
    const out: Record<string, any> = {};
    corrals.forEach((c) => {
      out[c.distance] = true;
    });
    return out;
  }, [corrals]);

  const distanceTotals = useMemo(
    () => (selectedDistance && total > 0 ? { [selectedDistance]: total } : {}),
    [selectedDistance, total],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleDistanceSelect = (d: string | undefined) => {
    setSelectedDistance(d);
    setWizardStep(0);
    setConfirmed(false);
    setEditableCorrals([]);
    setSelectedCorralId(undefined);
  };

  const handleNext = () => {
    const groups = computeCorralGroups(participants, strategy, fixedSize);
    if (groups.length === 0) {
      message.warning("No participants available to form corrals.");
      return;
    }
    setEditableCorrals(groups);
    setWizardStep(1);
  };

  const handlePreview = () => setWizardStep(2);

  const handleConfirm = async () => {
    if (!eventId || !selectedDistance) return;
    setConfirming(true);
    try {
      for (const ec of editableCorrals.filter(
        (c) => !c.is_default && c.bib_start > 0,
      )) {
        await addCorral({
          event_id: eventId,
          distance: selectedDistance,
          name: ec.name,
          bib_start: ec.bib_start,
          bib_end: ec.bib_end,
          max_capacity: ec.max_participants,
          start_time: ec.start_time,
        });
      }
      await runAutoAssign(eventId, selectedDistance);
      setConfirmed(true);
    } catch {
      message.error("Assignment failed. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const handleReset = () => {
    setWizardStep(0);
    setConfirmed(false);
    setEditableCorrals([]);
    if (eventId && selectedDistance) {
      fetchCorrals(eventId, selectedDistance).then((loaded) => {
        if (loaded && loaded.length > 0) {
          fetchSummary(eventId, selectedDistance);
          fetchAssignments(eventId, selectedDistance);
          setViewMode("manage");
        }
      });
    }
  };

  const handleSwitchToWizard = () => {
    if (isRegistrationOpen) {
      message.warning(
        "Corral assignment is only available after all registrations close for this distance.",
      );
      return;
    }
    if (eventId && selectedDistance) {
      fetchParticipants(eventId, selectedDistance, 1);
    }
    setViewMode("wizard");
    setWizardStep(0);
    setConfirmed(false);
    setEditableCorrals([]);
  };

  const handleReassign = (registrationId: string) => {
    if (!selectedNewCorralId || !eventId || !selectedDistance) return;
    runManualAssign(
      registrationId,
      selectedNewCorralId,
      eventId,
      selectedDistance,
    );
    setEditingAssignment(null);
    setSelectedNewCorralId(null);
  };

  const handleUnassign = (registrationId: string) => {
    if (!eventId || !selectedDistance) return;
    runUnassign(registrationId, eventId, selectedDistance);
  };

  const handleDeleteCorral = (corralId: string) => {
    if (!eventId) return;
    removeCorral(corralId, eventId, selectedDistance);
  };

  // ── Edit Corral Modal ─────────────────────────────────────────────────────

  const openEditCorral = (corralId: string) => {
    const corral = corrals.find((c) => c.id === corralId);
    if (!corral) return;
    setEditCorralModal(corral);
    editCorralForm.setFieldsValue({
      name: corral.name,
      bib_start: corral.bib_start,
      bib_end: corral.bib_end,
      max_capacity: (corral as any).max_capacity ?? null,
      start_time: (corral as any).start_time
        ? dayjs((corral as any).start_time, "HH:mm:ss")
        : null,
    });
  };

  const handleEditCorralSave = async () => {
    if (!editCorralModal || !eventId) return;
    try {
      const values = await editCorralForm.validateFields();

      // Overlap check: exclude the corral being edited
      const otherCorrals = corrals.filter(
        (c) =>
          c.id !== editCorralModal.id &&
          (!selectedDistance || c.distance === selectedDistance),
      );
      const overlapping = otherCorrals.find(
        (c) => values.bib_start <= c.bib_end && values.bib_end >= c.bib_start,
      );
      if (overlapping) {
        message.error(
          `BIB range ${values.bib_start}–${values.bib_end} overlaps with corral "${overlapping.name}" (${overlapping.bib_start}–${overlapping.bib_end}).`,
        );
        return;
      }

      // BIB range bounds check
      if (bibRanges.length > 0) {
        const minStart = Math.min(...bibRanges.map((r) => r.start));
        const maxEnd = Math.max(...bibRanges.map((r) => r.end));
        if (values.bib_start < minStart || values.bib_end > maxEnd) {
          message.error(
            `Corral BIB range must be within the defined BIB range ${minStart}–${maxEnd} for this distance.`,
          );
          return;
        }
      }

      setEditCorralSaving(true);
      const ok = await editCorral(editCorralModal.id, {
        name: values.name,
        bib_start: values.bib_start,
        bib_end: values.bib_end,
        max_capacity: values.max_capacity || null,
        start_time: values.start_time
          ? dayjs(values.start_time).format("HH:mm")
          : null,
      });
      if (ok) {
        setEditCorralModal(null);
        editCorralForm.resetFields();
        fetchSummary(eventId, selectedDistance);
        fetchCorrals(eventId, selectedDistance);
      }
    } catch (err: any) {
      if (err?.errorFields) return;
    } finally {
      setEditCorralSaving(false);
    }
  };

  // ── Assignment Table Columns ──────────────────────────────────────────────

  const assignmentColumnsBase: ColumnsType<CorralAssignment> = [
    {
      title: "#",
      width: 48,
      render: (_, __, i) => (
        <Text type="secondary">
          {(assignmentsPage - 1) * assignmentsPageSize + i + 1}
        </Text>
      ),
    },
    {
      title: "Participant",
      key: "participant",
      render: (_, r) => (
        <div>
          <Text strong>{r.participant.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.participant.email}
          </Text>
        </div>
      ),
    },
    {
      title: "BIB",
      dataIndex: "bib_number",
      key: "bib_number",
      width: 80,
      render: (bib) =>
        bib ? (
          <Text strong style={{ color: "#1890ff", fontSize: 15 }}>
            {bib}
          </Text>
        ) : (
          <Tag color="warning">No BIB</Tag>
        ),
    },
    {
      title: "Timing Certificate",
      key: "timing",
      width: 160,
      render: (_, record) => {
        if (
          !record.timing_certificate_category &&
          !record.expected_finish_time
        ) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <div>
            {record.timing_certificate_category && (
              <Tag color="purple" style={{ fontSize: 11, marginBottom: 2 }}>
                <TrophyOutlined style={{ marginRight: 3 }} />
                {record.timing_certificate_category}
              </Tag>
            )}
            {record.expected_finish_time && (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <ClockCircleOutlined style={{ marginRight: 3 }} />
                  {record.completed_race_time} ({record.expected_finish_time}{" "}
                  min)
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Corral",
      key: "corral",
      width: 160,
      render: (_, record) => {
        if (editingAssignment === record.registration_id) {
          return (
            <Space size={4} wrap>
              <Select
                size="small"
                value={selectedNewCorralId ?? undefined}
                onChange={setSelectedNewCorralId}
                style={{ width: 120 }}
                placeholder="Select"
                options={corrals
                  .filter((c) => !selectedDistance || c.distance === selectedDistance)
                  .map((c) => ({
                    label: `${c.name} (${c.bib_start}-${c.bib_end})`,
                    value: c.id,
                  }))}
              />
              <Button
                size="small"
                type="primary"
                disabled={!selectedNewCorralId}
                onClick={() => handleReassign(record.registration_id)}
              >
                OK
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setEditingAssignment(null);
                  setSelectedNewCorralId(null);
                }}
              >
                X
              </Button>
            </Space>
          );
        }
        if (record.corral_name) {
          const idx = corrals.findIndex((c) => c.id === record.corral_id);
          const p = CORRAL_PALETTE[idx >= 0 ? idx % CORRAL_PALETTE.length : 0];
          return (
            <Tag
              style={{
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 12,
                background: p.bg,
                borderColor: p.border,
                color: p.text,
              }}
            >
              Corral {record.corral_name}
            </Tag>
          );
        }
        return <Tag color="default">Unassigned</Tag>;
      },
    },
  ];

  const assignmentActionsColumn: ColumnsType<CorralAssignment>[number] = {
    title: "Actions",
    key: "actions",
    width: 100,
    render: (_, record) => (
      <Space size={4}>
        <Tooltip title="Change corral">
          <Button
            size="small"
            type="text"
            icon={<SwapOutlined />}
            onClick={() => {
              setEditingAssignment(record.registration_id);
              setSelectedNewCorralId(record.corral_id);
            }}
          />
        </Tooltip>
        {record.corral_id && (
          <Popconfirm
            title="Remove corral assignment?"
            onConfirm={() => handleUnassign(record.registration_id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Unassign corral">
              <Button size="small" type="text" danger icon={<UndoOutlined />} />
            </Tooltip>
          </Popconfirm>
        )}
      </Space>
    ),
  };

  const assignmentColumns: ColumnsType<CorralAssignment> =
    readOnlyAssignments || isRegistrationOpen
      ? assignmentColumnsBase
      : [...assignmentColumnsBase, assignmentActionsColumn];

  // ── Manage View ───────────────────────────────────────────────────────────

  const renderManageView = () => {
    const breakdown = (summary?.corral_breakdown ?? []).filter(
      (item) => !selectedDistance || item.distance === selectedDistance,
    );

    return (
      <div>
        {/* Registration still open — block assignment */}
        {isRegistrationOpen && latestRegistrationClose && (
          <Alert
            type="warning"
            showIcon
            message="Registration Period Not Yet Closed"
            description={`Corral assignment is only available after all registrations close for this distance. The last registration window closes on ${latestRegistrationClose}.`}
            style={{ marginBottom: 20 }}
          />
        )}

        {/* All assigned — completion banner */}
        {isFullyAssigned && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message={`All ${summary?.total} participants assigned for ${selectedDistance}`}
            description="Corral assignment is complete. You can still move individual participants between corrals using the swap icon in the assignments table below."
            style={{ marginBottom: 20 }}
          />
        )}

        {/* Corral Cards */}
        <Card
          bordered={false}
          style={{ marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Text strong style={{ fontSize: 16 }}>
                  Corrals — {selectedDistance}
                </Text>
                {loadingSummary ? (
                  <Skeleton.Input active size="small" style={{ width: 90 }} />
                ) : summary && (summary.total ?? 0) > 0 ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                      {summary.assigned} / {summary.total} assigned
                    </Text>
                    <Progress
                      percent={Math.round((summary.assigned / summary.total) * 100)}
                      size="small"
                      showInfo={false}
                      strokeColor={isFullyAssigned ? "#16a34a" : "#2563eb"}
                      style={{ width: 80, margin: 0 }}
                    />
                  </div>
                ) : null}
              </div>
              <Space wrap>
                {breakdown.length > 0 && (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => setLayoutModalOpen(true)}
                  >
                    View Layout
                  </Button>
                )}
                {!readOnlyAssignments && !isFullyAssigned && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalOpen(true)}
                    disabled={isRegistrationOpen}
                  >
                    Add Corral
                  </Button>
                )}
              </Space>
            </div>
          }
        >
          {loadingCorrals ? (
            <Row gutter={[16, 16]}>
              {[1, 2, 3].map((i) => (
                <Col key={i} xs={24} sm={12} md={8}>
                  <Skeleton active paragraph={{ rows: 3 }} />
                </Col>
              ))}
            </Row>
          ) : breakdown.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0" }}>
              <TeamOutlined
                style={{
                  fontSize: 40,
                  color: "#d1d5db",
                  display: "block",
                  marginBottom: 12,
                }}
              />
              <Text
                strong
                style={{
                  fontSize: 15,
                  color: "#6b7280",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                No Corrals Set Up Yet
              </Text>
              <Text
                type="secondary"
                style={{
                  display: "block",
                  marginBottom: 20,
                  maxWidth: 320,
                  margin: "0 auto 20px",
                }}
              >
                Group participants by pace and BIB range to manage wave starts.
              </Text>
              {!readOnlyAssignments && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="large"
                  onClick={handleSwitchToWizard}
                >
                  Create Corrals with Wizard
                </Button>
              )}
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {breakdown.map((item: CorralBreakdownItem, idx: number) => {
                const p = CORRAL_PALETTE[idx % CORRAL_PALETTE.length];
                const capacity =
                  item.max_capacity ?? item.bib_end - item.bib_start + 1;
                const pct =
                  capacity > 0
                    ? Math.round((item.assigned / capacity) * 100)
                    : 0;
                const isSelected = selectedCorralId === item.corral_id;

                return (
                  <Col key={item.corral_id} xs={24} sm={12} md={8} lg={6}>
                    <div
                      onClick={() => {
                        const willSelect = !isSelected;
                        setSelectedCorralId(
                          willSelect ? item.corral_id : undefined,
                        );
                        if (willSelect && eventId) {
                          fetchAssignments(
                            eventId,
                            selectedDistance,
                            item.corral_id,
                          );
                          setTimeout(() => {
                            assignmentsRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          }, 150);
                        } else if (eventId) {
                          fetchAssignments(eventId, selectedDistance);
                        }
                      }}
                      style={{
                        backgroundColor: isSelected ? p.bg : "#fff",
                        border: `${isSelected ? 2 : 1.5}px solid ${isSelected ? p.accent : p.border}`,
                        borderRadius: 12,
                        padding: "14px 16px",
                        cursor: "pointer",
                        transition: "all 0.18s",
                        boxShadow: isSelected
                          ? `0 4px 16px ${p.accent}28`
                          : "0 1px 4px rgba(0,0,0,0.06)",
                      }}
                    >
                      {/* Header: name + actions */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: p.accent,
                              flexShrink: 0,
                              marginTop: 2,
                            }}
                          />
                          <Text
                            strong
                            style={{
                              fontSize: 14,
                              color: p.text,
                              lineHeight: 1.3,
                            }}
                          >
                            {item.name}
                          </Text>
                          {item.assigned > 0 && item.assigned >= capacity && (
                            <Tag
                              style={{
                                fontSize: 10,
                                lineHeight: "16px",
                                padding: "0 5px",
                                margin: 0,
                                borderRadius: 4,
                                background: p.bg,
                                borderColor: p.accent,
                                color: p.accent,
                              }}
                            >
                              Full
                            </Tag>
                          )}
                        </div>
                        {!readOnlyAssignments && (
                          <Space size={2} style={{ flexShrink: 0 }}>
                            <Tooltip title="Edit corral">
                              <Button
                                size="small"
                                type="text"
                                icon={<EditOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditCorral(item.corral_id);
                                }}
                              />
                            </Tooltip>
                            <Popconfirm
                              title="Delete this corral? All assignments will be removed."
                              onConfirm={(e) => {
                                e?.stopPropagation();
                                handleDeleteCorral(item.corral_id);
                              }}
                              onCancel={(e) => e?.stopPropagation()}
                              okText="Delete"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                size="small"
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Popconfirm>
                          </Space>
                        )}
                      </div>

                      {/* BIB range + start time */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <Tag
                          style={{
                            borderRadius: 4,
                            fontSize: 11,
                            margin: 0,
                            background: p.bg,
                            borderColor: p.border,
                            color: p.text,
                            fontWeight: 600,
                          }}
                        >
                          BIB {item.bib_start}–{item.bib_end}
                        </Tag>
                        {item.start_time && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            <ClockCircleOutlined style={{ marginRight: 3 }} />
                            {item.start_time}
                          </Text>
                        )}
                      </div>

                      {/* Progress bar */}
                      <Progress
                        percent={pct}
                        strokeColor={p.accent}
                        trailColor={isSelected ? `${p.accent}20` : "#f0f0f0"}
                        showInfo={false}
                        size="small"
                        style={{ marginBottom: 6 }}
                      />

                      {/* Stats row */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Text style={{ fontSize: 12 }}>
                          <Text strong style={{ color: p.accent }}>
                            {item.assigned}
                          </Text>
                          <Text type="secondary"> / {capacity} assigned</Text>
                        </Text>
                        <Text
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color:
                              pct >= 90
                                ? "#dc2626"
                                : pct >= 70
                                  ? "#d97706"
                                  : p.accent,
                          }}
                        >
                          {pct}%
                        </Text>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card>

        {/* Assignments Table */}
        <div ref={assignmentsRef}>
          <Card
            bordered={false}
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
            title={
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Text strong style={{ fontSize: 16 }}>
                  {selectedCorralId
                    ? `Assignments — Corral ${breakdown.find((b) => b.corral_id === selectedCorralId)?.name ?? ""}`
                    : "All Assignments"}
                </Text>
                {selectedCorralId && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedCorralId(undefined);
                      if (eventId) fetchAssignments(eventId, selectedDistance);
                    }}
                  >
                    Show All
                  </Button>
                )}
              </div>
            }
          >
            <Table
              columns={assignmentColumns}
              dataSource={assignments}
              rowKey="registration_id"
              loading={loadingAssignments}
              size="small"
              scroll={{ x: 850 }}
              pagination={{
                current: assignmentsPage,
                pageSize: assignmentsPageSize,
                total: assignmentsTotal,
                onChange: (page) => {
                  if (eventId)
                    fetchAssignments(
                      eventId,
                      selectedDistance,
                      selectedCorralId,
                      page,
                    );
                },
                showTotal: (t) => `${t} registrations`,
                showSizeChanger: false,
              }}
            />
          </Card>
        </div>

        {/* Create Corral Modal */}
        <CreateCorralModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          eventId={eventId}
          distance={selectedDistance ?? ""}
          existingCorrals={corrals.filter(
            (c) => !selectedDistance || c.distance === selectedDistance,
          )}
          bibRangesForDistance={bibRanges}
          onSuccess={() => {
            if (eventId && selectedDistance) {
              fetchCorrals(eventId, selectedDistance);
              fetchSummary(eventId, selectedDistance);
              fetchAssignments(eventId, selectedDistance);
            }
          }}
          onSubmit={addCorral}
        />

        {/* Edit Corral Modal */}
        <Modal
          title={`Edit Corral ${editCorralModal?.name ?? ""}`}
          open={!!editCorralModal}
          onCancel={() => {
            setEditCorralModal(null);
            editCorralForm.resetFields();
          }}
          destroyOnClose
          width={480}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setEditCorralModal(null);
                editCorralForm.resetFields();
              }}
            >
              Cancel
            </Button>,
            <Button
              key="save"
              type="primary"
              loading={editCorralSaving}
              onClick={handleEditCorralSave}
            >
              Save Changes
            </Button>,
          ]}
        >
          <Form
            form={editCorralForm}
            layout="vertical"
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="name"
              label="Corral Name"
              rules={[
                { required: true, message: "Required" },
                { max: 20, message: "Max 20 characters" },
              ]}
            >
              <Input placeholder="e.g. A, Elite" maxLength={20} />
            </Form.Item>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="bib_start"
                  label="BIB Start"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <InputNumber
                    min={1}
                    className="w-full"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="bib_end"
                  label="BIB End"
                  rules={[
                    { required: true, message: "Required" },
                    ({ getFieldValue }) => ({
                      validator(_, val) {
                        if (!val || val >= getFieldValue("bib_start"))
                          return Promise.resolve();
                        return Promise.reject(
                          new Error("Must be >= BIB Start"),
                        );
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={1}
                    className="w-full"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="max_capacity" label="Max Capacity">
                  <InputNumber
                    min={1}
                    placeholder="Unlimited"
                    className="w-full"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="start_time" label="Start Time">
                  <TimePicker
                    format="HH:mm"
                    placeholder="e.g. 09:00"
                    style={{ width: "100%" }}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* Layout Preview Modal */}
        <Modal
          title={`Corral Layout — ${selectedDistance}`}
          open={layoutModalOpen}
          onCancel={() => setLayoutModalOpen(false)}
          destroyOnClose
          width={900}
          footer={null}
        >
          <CorralPreview
            editableCorrals={breakdown.map((item, idx) => ({
              key: item.corral_id,
              name: item.name,
              bib_start: item.bib_start,
              bib_end: item.bib_end,
              max_participants: item.max_capacity ?? (item.bib_end - item.bib_start + 1),
              start_time: item.start_time ?? undefined,
              color: CORRAL_PALETTE[idx % CORRAL_PALETTE.length].accent,
              is_default: false,
              participants: Array(item.assigned).fill({}) as any[],
            }))}
            distance={selectedDistance ?? ""}
            eventName={event?.name}
            onBack={() => {}}
            onConfirm={() => {}}
            confirming={false}
            readOnly
            onClose={() => setLayoutModalOpen(false)}
          />
        </Modal>
      </div>
    );
  };

  // ── Main Render ───────────────────────────────────────────────────────────

  return (
    <div
      style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}
      className="p-16 lg:p-10"
    >
      {/* Page Header */}
      <div
        style={{ backgroundColor: "#fff", borderBottom: "1px solid #f1f5f9" }}
      >
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          {eventLoading ? (
            <Skeleton.Input active size="large" style={{ width: 260 }} />
          ) : (
            <>
              <Title level={3} style={{ marginBottom: 0, lineHeight: 1.3 }}>
                {event?.name || "Corral Allocation"}
              </Title>
            </>
          )}
        </div>

        {viewMode === "wizard" &&
          !readOnlyAssignments &&
          corrals.length > 0 && (
            <div className="px-4 pb-4 pt-3 sm:px-6 lg:px-8 border-t border-gray-100" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Button
                onClick={() => {
                  if (eventId && selectedDistance) {
                    fetchSummary(eventId, selectedDistance);
                    fetchAssignments(eventId, selectedDistance);
                  }
                  setViewMode("manage");
                }}
              >
                ← Exit Wizard
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                Cancel and return to the existing corral setup
              </Typography.Text>
            </div>
          )}
      </div>

      {/* Main Content */}
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Distance selector for manage & initial loading */}
        {(viewMode === "manage" ||
          (viewMode === "loading" && !selectedDistance)) && (
          <Card
            bordered={false}
            style={{
              marginBottom: 20,
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
            }}
          >
            {eventLoading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <DistanceFilterCards
                distances={distances}
                selected={selectedDistance}
                onSelect={handleDistanceSelect}
                savedRanges={savedRangesForFilter}
                totalByDistance={distanceTotals}
              />
            )}
          </Card>
        )}

        {/* Unassigned participants call-to-action */}
        {viewMode === "manage" &&
          selectedDistance &&
          !isRegistrationOpen &&
          (summary?.unassigned ?? 0) > 0 &&
          corrals.filter((c) => c.distance === selectedDistance).length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${summary?.unassigned} participant${(summary?.unassigned ?? 0) !== 1 ? "s" : ""} still not assigned to a corral`}
              description='Click "Auto-Assign" in the corrals panel above to automatically place them, or use the swap icon in the assignments table to move them individually.'
              style={{ marginBottom: 16 }}
              action={
                <Button
                  size="small"
                  type="primary"
                  loading={assigning}
                  onClick={() =>
                    eventId &&
                    selectedDistance &&
                    runAutoAssign(eventId, selectedDistance)
                  }
                >
                  Auto-Assign Now
                </Button>
              }
            />
          )}

        {/* Registration close notice in the sticky breadcrumb area */}
        {isRegistrationOpen && selectedDistance && latestRegistrationClose && (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              backgroundColor: "#fffbeb",
              border: "1px solid #fcd34d",
              borderRadius: 8,
              padding: "8px 14px",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Tag color="orange" style={{ margin: 0 }}>
              {selectedDistance}
            </Tag>
            <span style={{ fontSize: 13, color: "#92400e" }}>
              Registration closes{" "}
              <strong>{latestRegistrationClose}</strong> — assignment locked
              until then.
            </span>
          </div>
        )}

        {/* Loading */}
        {viewMode === "loading" && selectedDistance && (
          <Card
            bordered={false}
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
          >
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        )}

        {/* Manage View */}
        {viewMode === "manage" && selectedDistance && renderManageView()}

        {/* Wizard View */}
        {viewMode === "wizard" && !readOnlyAssignments && (
          <>
            {confirmed ? (
              <Card
                bordered={false}
                style={{
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                  borderRadius: 16,
                }}
              >
                <Result
                  status="success"
                  title="Corrals Assigned!"
                  subTitle={`${editableCorrals.length} corrals have been created and participants automatically assigned for ${selectedDistance}. You can now review assignments, move individual participants between corrals, or run auto-assign again for any unassigned runners.`}
                  extra={[
                    <Button key="manage" type="primary" size="large" onClick={handleReset}>
                      Go to Corral Management
                    </Button>,
                    <Button
                      key="another"
                      size="large"
                      onClick={() => {
                        setSelectedDistance(undefined);
                        setWizardStep(0);
                        setConfirmed(false);
                        setEditableCorrals([]);
                        setViewMode("loading");
                      }}
                    >
                      Assign Another Distance
                    </Button>,
                  ]}
                />
              </Card>
            ) : (
              <>
                {/* Wizard Progress Steps */}
                <Card
                  bordered={false}
                  style={{ marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
                >
                  <Steps
                    current={wizardStep}
                    items={[
                      {
                        title: "Configure Strategy",
                        description: "Choose how to group participants",
                      },
                      {
                        title: "Review & Adjust",
                        description: "Edit corral names, times & capacities",
                      },
                      {
                        title: "Confirm",
                        description: "Preview and finalize",
                      },
                    ]}
                  />
                </Card>

                {/* Step-level context banner */}
                {wizardStep === 0 && (
                  <Alert
                    type="info"
                    showIcon
                    message="Step 1 — Configure Strategy"
                    description="Select the distance and choose how participants should be grouped into corrals. You can group by fixed size (e.g. 100 per corral) or by expected finish time."
                    style={{ marginBottom: 20 }}
                  />
                )}
                {wizardStep === 1 && (
                  <Alert
                    type="info"
                    showIcon
                    message="Step 2 — Review & Adjust Corrals"
                    description="Review the auto-generated corrals below. You can rename each corral, set a wave start time, and adjust the maximum capacity. Click 'View participants' to inspect who is in each group."
                    style={{ marginBottom: 20 }}
                  />
                )}
                {wizardStep === 2 && (
                  <Alert
                    type="info"
                    showIcon
                    message="Step 3 — Preview & Confirm"
                    description="This is a final preview of your corral layout. Once you confirm, corrals will be saved and participants automatically assigned. You can still make manual changes afterwards."
                    style={{ marginBottom: 20 }}
                  />
                )}

                {wizardStep === 0 && (
                  <CorralEstimation
                    distances={distances}
                    selectedDistance={selectedDistance}
                    onDistanceSelect={handleDistanceSelect}
                    savedRangesForFilter={savedRangesForFilter}
                    distanceTotals={distanceTotals}
                    eventLoading={eventLoading}
                    participants={participants}
                    loadingParticipants={loadingParticipants}
                    total={total}
                    strategy={strategy}
                    onStrategyChange={setStrategy}
                    fixedSize={fixedSize}
                    onFixedSizeChange={setFixedSize}
                    onNext={handleNext}
                  />
                )}

                {wizardStep === 1 && (
                  <CorralAssignmentStep
                    editableCorrals={editableCorrals}
                    onChange={setEditableCorrals}
                    onBack={() => setWizardStep(0)}
                    onPreview={handlePreview}
                  />
                )}

                {wizardStep === 2 && (
                  <CorralPreview
                    editableCorrals={editableCorrals}
                    distance={selectedDistance ?? ""}
                    eventName={event?.name}
                    onBack={() => setWizardStep(1)}
                    onConfirm={handleConfirm}
                    confirming={confirming}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CorralPage;
